import {SurveySubmission} from '#apollo/local/SurveyVar';
import {SurveyQuery, SurveyQuestionFragment} from '#apollo/schema';

type Survey = NonNullable<SurveyQuery['survey']>;
type Category = NonNullable<Survey['categories']>[0];

/**
 * Given an instance of a `survey`, and the answers to the given survey as saved
 * in the `reactiveVar`/`sessionStorage` surveyVar, this function returns the
 * indexes that should be used for the Wizard's initial state, allowing users to
 * resume taking the survey from the location they previously left at.
 *
 * If surveyAnswers is not present or an index cannot otherwise be derived, the
 * initial indexes will be 0.
 */
export const getInitialStateValues = ({
  survey,
  surveyAnswers,
}: {
  survey: Survey;
  surveyAnswers: SurveySubmission;
}) => {
  const answeredQuestions = Object.keys(surveyAnswers);
  const initialCategoryIndex = Math.max(
    survey.categories.findIndex(
      category => !!category.steps.find(({question}) => !answeredQuestions.includes(question.id)),
    ),
    0,
  );
  const initialQuestionIndex = Math.max(
    survey.categories[initialCategoryIndex].steps.findIndex(
      ({question}) => !answeredQuestions.includes(question.id),
    ),
    0,
  );

  return {initialCategoryIndex, initialQuestionIndex};
};

export const isLastQuestionInCategory = (stepNumber: number, category: Category) => {
  return category.steps.length - 1 === stepNumber;
};

export const isLastQuestionInSurvey = (
  stepNumber: number,
  categoryNumber: number,
  categories: Category[],
) => {
  const lastCategoryNumber = categories.length - 1;

  if (lastCategoryNumber !== categoryNumber) {
    return false;
  }

  const lastCategory = categories[lastCategoryNumber];
  const lastStepIndex = lastCategory.steps.length - 1;

  return lastStepIndex === stepNumber;
};

export const isFirstQuestionInSurvey = (
  firstCategory: Category,
  currentQuestion?: SurveyQuestionFragment,
) => {
  return !currentQuestion ? true : currentQuestion?.id === firstCategory.steps[0].question?.id;
};

export const isFirstQuestionInCategory = (stepNumber: number) => {
  return stepNumber === 0;
};
